import React, { useEffect, useState } from 'react';
import PageContainer from './PageContainer';
import api, { getCookie, ensureCsrf } from './utils/api';
import Loader from './components/Loader';
import ConfirmModal from './components/ConfirmModal';

export default function CareerAssociates() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', role: '' });
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState({}); // id => true while deleting
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const PAGE_SIZE = 12;
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  
  // Update user modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [updateForm, setUpdateForm] = useState({ password: '', role: '' });
  const [updating, setUpdating] = useState(false);

  const fetchUsers = async (pageParam = page, searchParam = search, roleParam = roleFilter) => {
    setLoading(true);
    try {
      let url = `/api/v1/users/?page=${pageParam}&page_size=${PAGE_SIZE}`;
      if (searchParam) url += `&search=${encodeURIComponent(searchParam)}`;
      if (roleParam) url += `&role=${encodeURIComponent(roleParam)}`;
      const res = await api.get(url);
      const data = res.data.results || [];
      setUsers(data);
      setHasNext(Boolean(res.data.next));
      setHasPrev(Boolean(res.data.previous));
    } catch (e) {
      console.error('Failed to fetch users', e.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ensureCsrf();
    api.get('/api/v1/roles/?page_size=200').then(res => {
      setRoles(res.data.results.filter(r => r.name !== 'Lead'));
    });
    fetchUsers(1, '', '');
  }, []);

  const handleFormChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreateUser = async e => {
    e.preventDefault();
    setLoading(true);
    try {
    await api.post('/api/v1/users/', {
        full_name: form.name,
        email: form.email,
        password: form.password,
        role: form.role
  });
      setMessages([{ tags: 'success', text: 'User created successfully.' }]);
      setForm({ name: '', email: '', password: '', role: '' });
      fetchUsers(1, search, roleFilter);
    } catch (err) {
      setMessages([{ tags: 'error', text: 'Error creating user.' }]);
    }
    setLoading(false);
  };

  const handleDeleteUser = async (user) => {
    setDeleting(d => ({ ...d, [user.id]: true }));
    setIsDeleting(true);
    // Optimistic UI: remove immediately, rollback on failure
    const prevUsers = users;
    setUsers(u => u.filter(x => x.id !== user.id));
    try {
      await api.delete(`/api/v1/users/${user.id}/`);
      setMessages(m => [{ tags: 'success', text: 'User deleted.' }, ...m.slice(0, 2)]);
      setDeleteTarget(null);
    } catch (err) {
      setMessages(m => [{ tags: 'error', text: 'Failed to delete user.' }, ...m.slice(0, 2)]);
      setUsers(prevUsers); // rollback
    } finally {
      setDeleting(d => { const cp = { ...d }; delete cp[user.id]; return cp; });
      setIsDeleting(false);
    }
  };

  const handleOpenUpdateModal = (user) => {
    setUserToUpdate(user);
    setUpdateForm({
      password: '',
      role: user.role || user.role_id || ''
    });
    setShowUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setUserToUpdate(null);
    setUpdateForm({ password: '', role: '' });
  };

  const handleUpdateFormChange = (e) => {
    setUpdateForm({ ...updateForm, [e.target.name]: e.target.value });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!userToUpdate) return;
    
    setUpdating(true);
    try {
      const updateData = {};
      if (updateForm.password.trim()) {
        updateData.password = updateForm.password;
      }
      if (updateForm.role) {
        updateData.role = updateForm.role;
      }

      if (Object.keys(updateData).length === 0) {
        setMessages([{ tags: 'error', text: 'Please provide password or role to update.' }]);
        return;
      }

      await api.patch(`/api/v1/users/${userToUpdate.id}/`, updateData);
      setMessages([{ tags: 'success', text: 'User updated successfully.' }]);
      handleCloseUpdateModal();
      fetchUsers(page, search, roleFilter); // Refresh the user list
    } catch (err) {
      setMessages([{ tags: 'error', text: 'Error updating user.' }]);
    } finally {
      setUpdating(false);
    }
  };

  // Debounced server search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchUsers(1, search, roleFilter); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Refetch when role filter changes
  useEffect(() => {
    setPage(1);
    fetchUsers(1, search, roleFilter);
  }, [roleFilter]);

  return (
    <PageContainer>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Manage Users</h1>
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Create New User</h2>
        <form className="grid grid-cols-1 md:grid-cols-4 gap-4" onSubmit={handleCreateUser}>
          <input type="text" name="name" placeholder="Name" required value={form.name} onChange={handleFormChange} className="col-span-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500" />
          <input type="email" name="email" placeholder="Email" required value={form.email} onChange={handleFormChange} className="col-span-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500" />
          <input type="password" name="password" placeholder="Password" required value={form.password} onChange={handleFormChange} className="col-span-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500" />
          <select name="role" required value={form.role} onChange={handleFormChange} className="col-span-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500">
            <option value="">Select Role</option>
            {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <div className="col-span-full">
            <button type="submit" className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md shadow" disabled={loading}>Create User</button>
          </div>
        </form>
      </div>
      {messages.length > 0 && (
        <div className="mb-6">
          {messages.map((m, i) => (
            <div key={i} className={`p-4 rounded-md ${m.tags === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{m.text}</div>
          ))}
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <input type="text" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} className="w-full md:w-1/2 border px-4 py-2 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500" />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="w-full md:w-1/4 border px-4 py-2 rounded-md shadow-sm focus:ring-2 focus:ring-indigo-500">
          <option value="">All Roles</option>
          {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <div className="space-y-4">
        {loading ? (
          <Loader />
        ) : (
          users.map(user => (
            <div key={user.id} className="user-card bg-white rounded-xl shadow p-4 flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <p className="text-indigo-600 font-semibold text-sm">{user.full_name}</p>
                <p className="text-gray-600 text-sm">{user.email}</p>
                <p className="text-gray-500 text-xs">Role: {(
                  user.role_name ||
                  (user.role && user.role.name) ||
                  (roles.find(r => String(r.id) === String(user.role)) || {}).name ||
                  ''
                )}</p>
                <p className="text-gray-400 text-xs">Created: {new Date(user.date_joined).toLocaleDateString()}</p>
              </div>
              <div className="mt-2 md:mt-0 flex gap-2">
                <button
                  type="button"
                  onClick={() => handleOpenUpdateModal(user)}
                  className="text-xs px-3 py-1 rounded-md border bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200 transition-colors"
                  title="Update user"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(user)}
                  disabled={!!deleting[user.id]}
                  className={`text-xs px-3 py-1 rounded-md border transition-colors ${deleting[user.id] ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-red-50 text-red-600 hover:bg-red-100 border-red-200'}`}
                  title="Delete user"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="flex justify-center items-center gap-4 mt-10">
        <button disabled={!hasPrev || loading} onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchUsers(p, search, roleFilter); }} className={`px-4 py-2 rounded-md border text-sm ${hasPrev ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>Prev</button>
        <span className="text-sm text-gray-500">Page {page}</span>
        <button disabled={!hasNext || loading} onClick={() => { const p = page + 1; setPage(p); fetchUsers(p, search, roleFilter); }} className={`px-4 py-2 rounded-md border text-sm ${hasNext ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>Next</button>
      </div>

      {/* Update User Modal */}
      {showUpdateModal && userToUpdate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Update User: {userToUpdate.full_name}
            </h2>
            <form onSubmit={handleUpdateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password (leave empty to keep current password)
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={updateForm.password}
                    onChange={handleUpdateFormChange}
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    name="role"
                    value={updateForm.role}
                    onChange={handleUpdateFormChange}
                    required
                    className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select Role</option>
                    {roles.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={updating}
                  className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                    updating 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {updating ? 'Updating...' : 'Update User'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseUpdateModal}
                  disabled={updating}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete User"
        message={`Are you sure you want to delete user "${deleteTarget ? deleteTarget.full_name || deleteTarget.email : ''}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={() => handleDeleteUser(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        danger={true}
      />
  </PageContainer>
  );
}
